import { useState, useRef, useEffect, useCallback, memo } from 'react'

import './BottomBar.css'

// Components
import ChordProgressionModal from './ChordProgressionModal'
import TransposeControl from './TransposeControl'
import { UnifiedPopup } from './UnifiedPopup'
import UnifiedTextBox from './UnifiedTextBox'
import { UserMenu } from './auth/UserMenu'
import { GuitarLimitBadge } from './auth/GuitarLimitBadge'
import { PremiumBadge } from './auth/PremiumBadge'
import { SaveModal } from './SaveModal'

// Auth and limitations
import { useAuth } from '../auth/AuthContext'
import { LimitationEngine } from '../utils/limitations'

// Types
import type { 
  GuitarArrangement, 
  Note,
  ProgressionMetadata
} from '../presets/presetSchema'
import type { ParsedChord } from '../utils/chordParser'
import type { UnifiedTextBoxRef } from './UnifiedTextBox'

// API
import { 
  saveProgression 
} from '../presets/presetAPI'

// Constants and utilities
import { SOUND_TYPES, SOUND_TYPE_LABELS } from '../utils/audio'
import { parseChordProgression } from '../utils/chordParser'
import { 
  createProgression,
  generatePresetName
} from '../utils/presetHelpers'
/**
 * Props interface for the BottomBar component
 */
interface BottomBarProps {
  onAddGuitar: () => void;
  soundType: string;
  onSoundTypeChange: (soundType: string) => void;
  guitars: GuitarArrangement[];
  onPresetNameChange: (name: string | null) => void;
  onGenerateFromChords: (chords: ParsedChord[]) => void;
  onReplaceFromArrangements: (arrangements: GuitarArrangement[], progressionMetadata?: { accessLevel?: string }) => void;
  onTranspose: (note: Note) => void;
  onShowUpgrade?: (feature: string, message?: string) => void;
  onShowLoginModal?: () => void;
  onShowSubscriptionManager?: () => void;
}

/**
 * BottomBar component providing application controls including:
 * - Add guitar button and transpose controls (left)
 * - Unified text box for chord input and preset search (center)
 * - Sound type selector and preset save controls (right)
 * 
 * Features modal positioning to prevent off-screen cutoff and handles
 * keyboard shortcuts for improved user experience.
 */
export function BottomBar({ 
  onAddGuitar, 
  soundType,
  onSoundTypeChange,
  guitars,
  onPresetNameChange,
  onGenerateFromChords,
  onReplaceFromArrangements,
  onTranspose,
  onShowUpgrade,
  onShowLoginModal,
  onShowSubscriptionManager
}: BottomBarProps) {
  // Auth context for limitation checks
  const { user } = useAuth();
  
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [showSoundModal, setShowSoundModal] = useState<boolean>(false);
  const [showChordModal, setShowChordModal] = useState<boolean>(false);
  const [savePresetName, setSavePresetName] = useState<string>('');
  
  // Unified text box state
  const [showUnifiedPopup, setShowUnifiedPopup] = useState<boolean>(false);
  const [popupInput, setPopupInput] = useState<string>('');
  
  const soundDropdownRef = useRef<HTMLDivElement>(null);
  const unifiedTextBoxRef = useRef<UnifiedTextBoxRef>(null);

  // Simple modal positioning to prevent off-screen cutoff
  const adjustModalPosition = useCallback(() => {
    // Adjust sound dropdown position  
    if (showSoundModal && soundDropdownRef.current) {
      const dropdown = soundDropdownRef.current.querySelector('.preset-dropdown-content') as HTMLElement;
      if (dropdown) {
        const rect = dropdown.getBoundingClientRect();
        
        // Toggle positioning for dropdown
        if (rect.right > window.innerWidth - 16) { // 16px padding
          dropdown.style.left = 'auto';
          dropdown.style.right = '0';
        } else {
          dropdown.style.left = '0';
          dropdown.style.right = 'auto';
        }
      }
    }
  }, [showSoundModal]);

  useEffect(() => {
    // Adjust on modal open and window resize
    if (showSoundModal) {
      adjustModalPosition();
    }

    window.addEventListener('resize', adjustModalPosition);
    return () => window.removeEventListener('resize', adjustModalPosition);
  }, [showSoundModal, adjustModalPosition]);

  // Close dropdowns/modals when clicking outside or pressing Escape
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (soundDropdownRef.current && !soundDropdownRef.current.contains(event.target as Node)) {
      setShowSoundModal(false);
    }
  }, []);

  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (showSoundModal) {
        setShowSoundModal(false);
      }
    }
  }, [showSoundModal]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleClickOutside, handleEscapeKey]);

  const handleSaveClick = useCallback(() => {
    const defaultName = generatePresetName(guitars);
    setSavePresetName(defaultName);
    setShowSaveModal(true);
    // Focus the input after modal opens
    setTimeout(() => {
      const input = document.querySelector('.preset-name-input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 100);
  }, [guitars]);

  const handleSaveWithMetadata = useCallback(async (name: string, metadata: Partial<ProgressionMetadata>) => {
    // Check preset saving limitation
    const userTier = user?.tier || 'free';
    
    // Get current preset count (we need to implement this)
    // For now, let's use a simple localStorage check
    const existingPresets = JSON.parse(localStorage.getItem('fret_progressions_list') || '[]');
    const presetLimitCheck = LimitationEngine.checkPresetLimit(existingPresets.length, userTier);
    
    if (!presetLimitCheck.allowed) {
      if (onShowUpgrade) {
        onShowUpgrade('presets');
      } else {
        alert(LimitationEngine.getUpgradeMessage('presets'));
      }
      return;
    }

    try {
      // Create progression with enhanced metadata
      const progression = createProgression(guitars, name);
      
      // Merge in the user-provided metadata
      progression.metadata = {
        ...progression.metadata,
        ...metadata
      };

      const result = await saveProgression(name, progression);
      if (result.success) {
        onPresetNameChange(name);
        setShowSaveModal(false);
      }
    } catch {
      // Silently handle errors
    }
  }, [guitars, onPresetNameChange, user, onShowUpgrade]);

  const handleChordProgressionGenerate = useCallback((chords: ParsedChord[]) => {
    if (onGenerateFromChords) {
      onGenerateFromChords(chords);
      // Removed disruptive notification message
    }
  }, [onGenerateFromChords]);

  // Unified text box handlers
  const handleUnifiedGenerate = useCallback((input: string) => {
    if (onGenerateFromChords) {
      // Parse the input string into chord objects
      const { chords, errors } = parseChordProgression(input);
      
      if (errors.length > 0) {
        // Error is already shown inline in the text input
        return;
      }
      
      if (chords.length === 0) {
        // No valid chords - this is also handled by inline validation
        return;
      }
      
      // Generate guitars from parsed chords
      onGenerateFromChords(chords);
      
      // Clear the input after successful generation
      setPopupInput('');
      if (unifiedTextBoxRef.current && unifiedTextBoxRef.current.clearInput) {
        unifiedTextBoxRef.current.clearInput(false); // Don't refocus to prevent popup reappearing
      }
      
      // Close popup after generation
      setShowUnifiedPopup(false);
    }
  }, [onGenerateFromChords]);

  const handleShowUnifiedPopup = useCallback((input: string) => {
    setPopupInput(input);
    setShowUnifiedPopup(true);
  }, []);

  const handleHideUnifiedPopup = useCallback(() => {
    setShowUnifiedPopup(false);
  }, []);

  return (
    <div className="bottom-bar">
      {/* Left side controls container */}
      <div className="left-controls">
        {/* Add Guitar Button with Badge */}
        <div className="add-guitar-button-container">
          <button 
            type="button" 
            className="add-guitar-button"
            onClick={onAddGuitar}
          >
            +
          </button>
          
          {/* Guitar Limit Badge */}
          <GuitarLimitBadge 
            currentCount={guitars.length} 
            className="guitar-limit-badge" 
          />
        </div>

        {/* Transpose Control */}
        <TransposeControl
          guitars={guitars}
          onTranspose={onTranspose}
        />
      </div>

      {/* Center controls container */}
      <div className="center-controls">
        {/* Unified Text Box with integrated Quick Create */}
        <UnifiedTextBox
          ref={unifiedTextBoxRef}
          onGenerate={handleUnifiedGenerate}
          onShowPopup={handleShowUnifiedPopup}
          onHidePopup={handleHideUnifiedPopup}
          value={popupInput}
          onInputChange={setPopupInput}
          placeholder="Type chords or search presets..."
          className="unified-text-box-container"
        />
      </div>

      {/* Right side controls container */}
      <div className="right-controls">
        {/* Sound Type Selector */}
        <div className="sound-controls" ref={soundDropdownRef}>
          <button
            type="button"
            className={`sound-selector-button ${showSoundModal ? 'active' : ''}`}
            onClick={() => setShowSoundModal(!showSoundModal)}
            title="Select sound type"
          >
            🔊
            <span className={`dropdown-arrow ${showSoundModal ? 'open' : ''}`}>▼</span>
          </button>
            {showSoundModal && (
              <div className="preset-dropdown-content">
                <div className="sound-grid">
                  {Object.entries(SOUND_TYPES).map(([key, value]) => {
                    // Check if this sound type is accessible to the user
                    const userTier = user?.tier || 'free';
                    const hasAccess = LimitationEngine.checkSoundTypeAccess(value, userTier);
                    
                    return (
                      <button
                        key={key}
                        type="button"
                        className={soundType === value ? 'active sound-button' : 'sound-button'}
                        onClick={() => {
                          if (hasAccess) {
                            onSoundTypeChange(value);
                            setShowSoundModal(false);
                          } else {
                            if (onShowUpgrade) {
                              onShowUpgrade('audio-types');
                            } else {
                              alert(LimitationEngine.getUpgradeMessage('audio-types'));
                            }
                          }
                        }}
                        style={{ 
                          opacity: hasAccess ? 1 : 0.6,
                          cursor: hasAccess ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {SOUND_TYPE_LABELS[value]}
                        {!hasAccess && <PremiumBadge inline />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        {/* Preset Controls */}
        <div className="preset-controls">
        {/* Save Preset Button */}
        <div className="save-button-container" style={{ position: 'relative' }}>
          <button 
            type="button" 
            className="preset-button save-button"
            onClick={handleSaveClick}
            title="Save current configuration as preset"
          >
            💾
          </button>
          
          {/* Enhanced Save Modal positioned above button */}
          {showSaveModal && user && (
            <SaveModal
              isVisible={showSaveModal}
              onClose={() => setShowSaveModal(false)}
              onSave={handleSaveWithMetadata}
              initialName={savePresetName}
              positioning="dropdown"
            />
          )}
          
          {/* Login prompt for unauthenticated users */}
          {showSaveModal && !user && (
            <div className="save-modal-overlay dropdown-positioning" onClick={() => setShowSaveModal(false)}>
              <div className="save-modal-content dropdown-content" onClick={(e) => e.stopPropagation()}>
                <h3>Login to Save</h3>
                <p className="save-login-message">
                  Sign in to save your guitar arrangements and access them from any device.
                </p>
                <div className="save-modal-buttons">
                  <button 
                    type="button" 
                    className="save-confirm-button"
                    onClick={() => {
                      setShowSaveModal(false);
                      onShowLoginModal?.();
                    }}
                  >
                    Sign In
                  </button>
                  <button 
                    type="button" 
                    className="save-cancel-button"
                    onClick={() => setShowSaveModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <UserMenu 
          onShowLoginModal={onShowLoginModal}
          onShowSubscriptionManager={onShowSubscriptionManager}
        />
      </div>
    </div>

      {/* Chord Progression Modal */}
      <ChordProgressionModal
        isOpen={showChordModal}
        onClose={() => setShowChordModal(false)}
        onGenerate={handleChordProgressionGenerate}
      />

      {/* Unified Popup */}
      <UnifiedPopup
        isVisible={showUnifiedPopup}
        onClose={handleHideUnifiedPopup}
        input={popupInput}
        onGenerate={handleUnifiedGenerate}
        onReplaceFromArrangements={onReplaceFromArrangements}
      />

    </div>
  );
}

// Memoize BottomBar component for performance optimization
// Re-renders only when props change (shallow comparison is sufficient for most props)
export default memo(BottomBar);
